import React, { useState, useEffect, useRef, ReactElement, ChangeEvent, MutableRefObject } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

import { UserProfilePicture, User } from '../../../types/entity.types';

import { getClassNames, retrieveFromLocalStorage, fileToBase64 } from '../../../utils/util';

import ProfilePicture from '../profile-picture/ProfilePicture';
import Button from '../button/Button';
import FilePicker from '../input/file-picker/FilePicker';
import UsernameValidation from '../validation/username-validation/UsernameValidation';
import PasswordValidation from '../validation/password-validation/PasswordValidation';
import PasswordInput from '../input/password-input/PasswordInput';

import './profile.scss';

type Props = {
  show: boolean;
  user?: User;
  signOut: () => void;
  updateSystemUser: (user: User) => void;
  showErrorToaster: (message: string) => void;
};

const Profile = ({ show, user, signOut, updateSystemUser, showErrorToaster }: Props): ReactElement => {
  const [username, setUsername] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmationPassword, setConfirmationPassword] = useState<string>('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [enteringUsername, setEnteringUsername] = useState<boolean>(false);
  const [enteringPassword, setEnteringPassword] = useState<boolean>(false);
  const [validUsername, setValidUsername] = useState<boolean>(true);
  const [validPassword, setValidPassword] = useState<boolean>(true);

  const fileRef: MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement>(null);

  useEffect((): void => {
    if (user && show) {
      const { username, email } = user;
      setUsername(username ?? '');
      setEmail(email ?? '');
      setPassword('');
      setConfirmationPassword('');
    }
  }, [user, show]);

  const handleSaveClick = async (): Promise<void> => {
    const externalReference: string = retrieveFromLocalStorage('user');
    let profilePicture: UserProfilePicture;

    if (file) {
      profilePicture = {
        base64String: await fileToBase64(file),
        name: file.name
      };
    } else {
      profilePicture = { uuid: user?.profilePicture as string };
    }

    const updatedUser: User = {
      externalReference,
      username,
      email,
      password: password ? password : undefined,
      profilePicture
    };
    updateSystemUser(updatedUser);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const files: FileList | null = event.target.files;

    if (!files || files.length === 0) return;

    const file: File | undefined = files[0];

    if (!file) return;

    if (file.size < 5000000) {
      setFile(file);
      return;
    }

    showErrorToaster('Profile picture must be less than 5MB');
  };

  return (
    <div className={getClassNames('profile', { show })} data-testid="profile">
      <p className="profile__header">My Profile</p>

      <div className="profile__image-container" data-testid="profile-image">
        <ProfilePicture
          src={file ? file : (user?.profilePicture as string)}
          alt={`${user?.username} - profile picture`}
        />
        <FilePicker
          fileRef={fileRef}
          fileType="image/*"
          onChange={handleFileChange}
          triggerElement={
            <div
              className="profile__image-container__icon"
              onClick={(): void => fileRef.current?.click()}
              data-testid="profile-upload-icon"
            >
              <FontAwesomeIcon icon={faPlus} />
            </div>
          }
        />
      </div>

      <div className="profile__input">
        <label htmlFor="profile-username">Username:</label>
        <input
          value={username}
          onFocus={(): void => setEnteringUsername(true)}
          onBlur={(): void => setEnteringUsername(false)}
          onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setUsername(target.value)}
          id="profile-username"
        />
        {(enteringUsername || (username && username !== user?.username)) && (
          <UsernameValidation username={username} setValidUsername={setValidUsername} />
        )}
      </div>

      <div className="profile__input">
        <label htmlFor="profile-email">Email Address:</label>
        <input
          value={email}
          onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setEmail(target.value)}
          id="profile-email"
        />
      </div>

      <div className="profile__input">
        <label htmlFor="password">Password:</label>
        <PasswordInput
          id="password"
          value={password}
          onFocus={(): void => setEnteringPassword(true)}
          onBlur={(): void => setEnteringPassword(false)}
          onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setPassword(target.value)}
          profile
        />
      </div>

      {(enteringPassword || password.length > 0) && (
        <div className="profile__input">
          <label htmlFor="confirmation-password">Confirm Password:</label>
          <PasswordInput
            id="confirmation-password"
            value={confirmationPassword}
            onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setConfirmationPassword(target.value)}
            profile
          />
          <PasswordValidation
            password={password}
            confirmationPassword={confirmationPassword}
            setValidPassword={setValidPassword}
          />
        </div>
      )}

      <div className="profile__button-container">
        <Button text="SAVE" onClick={handleSaveClick} disabled={!validUsername || (!!password && !validPassword)} />
        <hr className="profile__divider" />
        <Button text="SIGN OUT" onClick={signOut} />
      </div>
    </div>
  );
};

export default Profile;
